import type { VirtualSpaceBlockRow } from "@/lib/virtualspace/virtual-space-types";

/**
 * Block types that occupy a viewer surface and benefit from "theater" mode —
 * suspended in the middle of the viewport, page chrome dimmed around them.
 *
 * Mental model: YouTube's "T" key. When a media block becomes the dominant
 * visible block, it expands toward the viewport center and the surrounding
 * reading content + spaces rail fade back. Text/files stay at reading width
 * and never enter theater (they're for skimming, not focused viewing).
 *
 * Single source of truth so the renderer (which sets `data-vs-theater-eligible`)
 * and the observer hook (which decides who's most-centered) agree on which
 * blocks to track.
 */
export const PUBLIC_VIRTUAL_SPACE_THEATER_BLOCK_TYPES: ReadonlySet<string> =
  new Set([
    "hero_aerial",
    "hero_image",
    "aerial_image",
    "drone_video",
    "video",
    "matterport",
    "navvis_embed",
    "point_cloud",
    "lidar_viewer",
    "bim_viewer",
    "floor_plan_view",
    "google_3d_tiles",
    "google_map",
    "street_view",
    "external_embed",
    "location_card",
  ]);

/** Convenience predicate so callers don't reach into the constant directly. */
export function isTheaterEligibleBlock(block: VirtualSpaceBlockRow): boolean {
  return PUBLIC_VIRTUAL_SPACE_THEATER_BLOCK_TYPES.has(block.block_type);
}

/** Stable data-attr names so CSS + observer + renderer all agree. */
export const THEATER_ELIGIBLE_ATTR = "data-vs-theater-eligible";
export const THEATER_STATE_ATTR = "data-vs-theater";
export const THEATER_ROOT_STATE_ATTR = "data-vs-theater-active";
